import currency from 'currency.js';
import { Decimal } from 'decimal.js';

// COMPOSABLES
import { useCurrencyConfig } from '@/composables/useCurrency';

// TYPES
import {
  PaymentTypeCurrency,
  RouteProductType,
  RouteTransfer,
} from '@/types/RouteTransferType';
import { SeparatorDecimal } from '@/types/currency';

interface ICurrencyDecimalSeparator {
  from: string;
  to: string;
}

export const currenciesSymbol = {
  ALL: 'Lek',
  AFN: '؋',
  ARS: '$',
  AWG: 'ƒ',
  AUD: '$',
  AZN: '₼',
  BSD: '$',
  BBD: '$',
  BYN: 'Br',
  BZD: 'BZ$',
  BMD: '$',
  BOB: 'Bs.',
  BAM: 'KM',
  BWP: 'P',
  BGN: 'лв',
  BRL: 'R$',
  BND: '$',
  KHR: '៛',
  CAD: '$',
  KYD: '$',
  CLP: '$',
  CNY: '¥',
  COP: '$',
  CRC: '₡',
  HRK: 'kn',
  CUP: '₱',
  CZK: 'Kč',
  DKK: 'kr',
  DOP: 'RD$',
  XCD: '$',
  EGP: '£',
  SVC: '$',
  EUR: '€',
  FKP: '£',
  FJD: '$',
  GHS: '¢',
  GIP: '£',
  GTQ: 'Q',
  GGP: '£',
  GYD: '$',
  HNL: 'L',
  HKD: '$',
  HUF: 'Ft',
  ISK: 'kr',
  IDR: 'Rp',
  IRR: '﷼',
  IMP: '£',
  ILS: '₪',
  JMD: 'J$',
  JPY: '¥',
  JEP: '£',
  KZT: 'лв',
  KPW: '₩',
  KRW: '₩',
  KGS: 'лв',
  LAK: '₭',
  LBP: '£',
  LRD: '$',
  MKD: 'ден',
  MYR: 'RM',
  MUR: '₨',
  MXN: '$',
  MNT: '₮',
  MZN: 'MT',
  NAD: '$',
  NPR: '₨',
  ANG: 'ƒ',
  NZD: '$',
  NIO: 'C$',
  NGN: '₦',
  NOK: 'kr',
  OMR: '﷼',
  PKR: '₨',
  PAB: 'B/.',
  PYG: 'Gs',
  PEN: 'S/',
  PHP: '₱',
  PLN: 'zł',
  QAR: '﷼',
  RON: 'lei',
  RUB: '₽',
  SHP: '£',
  SAR: '﷼',
  RSD: 'Дин.',
  SCR: '₨',
  SGD: '$',
  SBD: '$',
  SOS: 'S',
  ZAR: 'R',
  LKR: '₨',
  SEK: 'kr',
  CHF: 'CHF',
  SRD: '$',
  SYP: '£',
  TWD: 'NT$',
  THB: '฿',
  TTD: 'TT$',
  TVD: '$',
  UAH: '₴',
  GBP: '£',
  USD: '$',
  UYU: '$U',
  UZS: 'лв',
  VEF: 'Bs',
  VEB: 'Bs.',
  VES: 'Bs.',
  VND: '₫',
  YER: '﷼',
  ZWD: 'Z$',
};

export const getAuxDecimalsToCurrency = (currencyCode: string) => {
  const currenciesWithoutDecimals = [
    'BIF',
    'CLP',
    'DJF',
    'GNF',
    'ISK',
    'JPY',
    'KMF',
    'KRW',
    'PYG',
    'RWF',
    'UGX',
    'VND',
    'VUV',
    'XAF',
    'XOF',
    'XPF',
    'COP',
  ];

  return currenciesWithoutDecimals.includes(currencyCode) ? 0 : 2;
};

function currencySeparator(currency: string): string {
  switch (currency) {
    case 'PEN':
      return ',';
    case 'MXN':
      return ',';
    case 'USD':
      return ',';
    default:
      return '.';
  }
}

function currencyDecimalSeparator(currency: string): ICurrencyDecimalSeparator {
  switch (currency) {
    case 'PEN':
      return { from: '.', to: '.' };
    case 'MXN':
      return { from: '.', to: '.' };
    case 'USD':
      return { from: '.', to: '.' };
    default:
      return { from: '.', to: ',' };
  }
}

export const currencySymbol = (currency: string): string => {
  switch (currency) {
    case 'PEN':
      return 'S/';
    case 'EUR':
      return '€';
    case 'BRL':
      return 'R$';
    case 'CNY':
      return '¥';
    default:
      return '$';
  }
};

export function clearDecimalNumber(value: string | number) {
  return Number(value.toString().replace(/[^0-9]/g, ''));
}

export function formatCurrencyExchangeRate(
  value: string | number,
  currency: string
): string {
  const { from, to } = currencyDecimalSeparator(currency);
  const amount = String(value)
    .replace(from, to)
    .replace(/\B(?=(\d{3})+(?!\d))/g, currencySeparator(currency));

  return `${currencySymbol(currency)} ${amount} ${currency}`;
}

export function formatCurrency(
  val: string | number,
  currency: string,
  decimals?: number
): string {
  if (!val) {
    return '0';
  }

  if (decimals === undefined) {
    decimals = getAuxDecimalsToCurrency(currency);
  }

  const { from, to } = currencyDecimalSeparator(currency);
  const value = typeof val === 'string' ? parseFloat(val) : val;

  const amount = String(truncateDecimal(value, decimals))
    .replace(from, to)
    .replace(/\B(?=(\d{3})+(?!\d))/g, currencySeparator(currency));

  return `${currencySymbol(currency)} ${amount} ${currency}`;
}

export function formatAmountWithCurrency(
  value: number,
  currency: string,
  decimals = 0
): string {
  const { from, to } = currencyDecimalSeparator(currency);

  const amount = (
    decimals ? value.toFixed(decimals) : Math.floor(Number(value)).toString()
  )
    .replace(from, to)
    .replace(/\B(?=(\d{3})+(?!\d))/g, currencySeparator(currency));

  return `${currency} ${amount}`;
}

export const formatAmount = (
  val: string | number,
  currency: string
): string => {
  const { from, to } = currencyDecimalSeparator(currency);
  const value = typeof val === 'string' ? parseFloat(val) : val;
  const amount = value
    .toFixed(0)
    .replace(from, to)
    .toString()
    .replaceAll(/[a-zA-Z]|[!@#%^&*]/g, '')
    .replace(/\B(?=(\d{3})+(?!\d))/g, currencySeparator(currency));

  return amount;
};

type CurrencyOption = {
  country: string;
  currency: string;
  language: string;
  maximumFractionDigits?: number;
};

export function formatAmountByRoute(
  amount = 0,
  route: RouteTransfer,
  maximumFractionDigits = 0
): string {
  return currencyFormatter({
    country: route?.country?.alpha2Code,
    currency: route?.currency?.code,
    language: route?.defaultLanguage?.toLowerCase(),
    maximumFractionDigits,
  }).format(amount);
}

export function currencyFormatter(
  options: CurrencyOption
): Record<string, any> {
  const language = options.language || 'es';
  const country = options.country || 'cl';
  const currency = options.currency || 'CLP';
  const maximumFractionDigits = options.maximumFractionDigits || 0;
  const minimumFractionDigits = 0;

  const localeOptions = {
    currency,
    maximumFractionDigits,
    minimumFractionDigits,
  };
  const locale = `${language}-${country}`;
  const amountFormat = new Intl.NumberFormat(locale, { ...localeOptions });

  return {
    format(amount: number | string) {
      const amountRound = Math.floor(Number(amount));

      const types = amountFormat.formatToParts(amountRound);
      const currencyCode =
        types && types.find((type) => type.type === 'currency');
      const hideCurrencyFromParams =
        currencyCode &&
        options.currency &&
        currencyCode.value === options.currency.toString();
      const formattedAmount = amountFormat.format(amountRound);

      return hideCurrencyFromParams
        ? formattedAmount
        : formattedAmount + ' ' + options.currency;
    },
  };
}

interface CostCalculationParams {
  isFreeTransfer: boolean;
  fee: number;
  markupAmount: number;
  discountAmountUsd: string | number;
  originRate: number;
  originSpread: number;
  originCurrency: string;
  productType?: RouteProductType;
  paymentType?: PaymentTypeCurrency;
}

export function totalCostString(params: CostCalculationParams): string {
  let total = 0;

  if (params.isFreeTransfer) {
    total = calculateCostWithDiscount(
      params.markupAmount,
      params.fee,
      params.discountAmountUsd,
      params.originRate,
      params.originSpread
    );
  } else if (params.fee) {
    total = params.fee;
  }

  return currencyConfigFormat({
    amount: total,
    currencyCode: params.originCurrency,
    hasCode: true,
    paymentType: params.paymentType,
    productType: params.productType,
    decimalLimitDefault: 2,
  });
}

function calculateCostWithDiscount(
  markupAmount: number,
  fee: number,
  discountAmountUsd: string | number,
  originRate: number,
  originSpread: number
): number {
  let cost = markupAmount;
  const discountThreshold = 20 * originSpread;
  const discountApplied = Number(discountAmountUsd) >= discountThreshold;
  if (discountApplied) {
    const discount = (fee - markupAmount) / originRate - discountThreshold;
    cost += discount * originRate;
  }
  return cost;
}

export function formatDecimalAmount(
  value: string | number,
  currency = ''
): number {
  const decimals = Boolean(getAuxDecimalsToCurrency(currency));

  return Number(!decimals ? Math.floor(Number(value)) : value);
}

export function separatorDecimal(currency: string): SeparatorDecimal {
  const pointDecimalSeparator = ['PEN', 'USD', 'MXN'].includes(currency);

  return {
    decimalLimit: getAuxDecimalsToCurrency(currency),
    thousandSeparator: pointDecimalSeparator ? ',' : '.',
    decimalSeparator: pointDecimalSeparator ? '.' : ',',
  };
}

/**
 * Normaliza un importe con separadores locales a la forma canónica
 * "entero.decimales" (siempre `.` como separador decimal, sin separadores de
 * miles). Distingue agrupación de miles de decimal por el largo del grupo
 * detrás del último separador, sin depender de cuál char sea `.` o `,`:
 *
 *   - "1.234"        (T=".") → "1234"     (3 dígitos y un solo separador → miles)
 *   - "10.5"  (T=".", D=",") → "10.5"     (1 dígito ≤ decimalLimit → decimal aunque el char sea el "de miles")
 *   - "1.234,56"     (T=".") → "1234.56"  (2 separadores → el último es decimal)
 *   - "1.234.56"     (T=".") → "1234.56"  (2 separadores → el último es decimal)
 *   - "10."   (T=".", D=",") → "10."      (separador trailing sin dígitos: preserva estado de tipeo)
 *
 * Ver BMB-3755: el parser anterior hacía `raw.split(thousandSeparator).join('')`
 * sobre el string crudo, y para EUR (donde el default local usa `.` como miles)
 * eliminaba el punto que el usuario tipeaba como decimal antes de que llegara
 * al modelo.
 */
export function normalizeAmountSeparators(
  raw: string | number | null | undefined,
  config: {
    thousandSeparator?: string;
    decimalSeparator?: string;
    decimalLimit: number;
  }
): string {
  const value = String(raw ?? '').replace(/\s/g, '');
  if (!value) return '';

  const { thousandSeparator = '', decimalSeparator = '', decimalLimit } = config;

  if (!decimalLimit || decimalLimit <= 0) {
    return value.replace(/\D/g, '');
  }

  const seps = Array.from(
    new Set([thousandSeparator, decimalSeparator].filter(Boolean))
  );
  const escapedSeps = seps.map((s) =>
    s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );
  const cleaned = value.replace(
    new RegExp(`[^\\d${escapedSeps.join('')}]`, 'g'),
    ''
  );
  if (!cleaned) return '';

  const sepChars = new Set(seps);
  const groups: string[] = [];
  const usedSeps: string[] = [];
  let current = '';
  for (const ch of cleaned) {
    if (sepChars.has(ch)) {
      groups.push(current);
      usedSeps.push(ch);
      current = '';
    } else {
      current += ch;
    }
  }
  groups.push(current);

  if (usedSeps.length === 0) {
    return groups[0];
  }

  const onlyThousandSepUsed =
    Boolean(thousandSeparator) &&
    usedSeps.every((sep) => sep === thousandSeparator);

  if (onlyThousandSepUsed && isPureThousandsGrouping(groups)) {
    return groups.join('');
  }

  if (onlyThousandSepUsed && isIncompleteThousandsGrouping(groups)) {
    return groups.join('');
  }

  const lastGroup = groups[groups.length - 1];
  const integer = groups.slice(0, -1).join('');
  if (lastGroup.length === 0) {
    return `${integer}.`;
  }
  const decimals = lastGroup.slice(0, decimalLimit);
  return `${integer}.${decimals}`;
}

const THOUSANDS_GROUP_LENGTH = 3;
const MIN_LEADING_GROUP_LENGTH = 1;

function hasValidLeadingGroup(leadingGroup: string): boolean {
  return (
    leadingGroup.length >= MIN_LEADING_GROUP_LENGTH &&
    leadingGroup.length <= THOUSANDS_GROUP_LENGTH
  );
}

function isPureThousandsGrouping(groups: string[]): boolean {
  if (groups.length < 2) return false;
  const [leadingGroup, ...trailingGroups] = groups;
  if (!hasValidLeadingGroup(leadingGroup)) return false;
  return trailingGroups.every(
    (group) => group.length === THOUSANDS_GROUP_LENGTH
  );
}

function isIncompleteThousandsGrouping(groups: string[]): boolean {
  if (groups.length < 2) return false;

  const [leadingGroup, ...trailingGroups] = groups;
  if (!hasValidLeadingGroup(leadingGroup)) return false;

  const middleGroups = trailingGroups.slice(0, -1);
  const middleAreWellFormed = middleGroups.every(
    (group) => group.length === THOUSANDS_GROUP_LENGTH
  );
  if (!middleAreWellFormed) return false;

  const lastGroup = trailingGroups[trailingGroups.length - 1];
  return lastGroup.length > THOUSANDS_GROUP_LENGTH;
}

export function truncateDecimal(amount: number, decimalLimit: number) {
  decimalLimit = Number(decimalLimit);
  if (isNaN(decimalLimit)) {
    decimalLimit = 0;
  }

  const decimal = new Decimal(amount);
  const truncated = decimal.toDecimalPlaces(decimalLimit, Decimal.ROUND_DOWN);
  return truncated.toNumber();
}

interface UseCurrencyFormat {
  amount?: number;
  currencyCode: string;
  hasSymbol?: boolean;
  hasCode?: boolean;
  productType?: RouteProductType;
  paymentType?: PaymentTypeCurrency;
  decimalLimitDefault?: number;
}

export function currencyConfigFormat({
  amount = 0,
  currencyCode,
  hasCode = false,
  hasSymbol = true,
  productType,
  paymentType,
  decimalLimitDefault,
}: UseCurrencyFormat): string {
  const { decimalLimit, decimalSeparator, thousandSeparator } =
    useCurrencyConfig({ currency: currencyCode, productType, paymentType });

  const precision =
    decimalLimitDefault != undefined ? decimalLimitDefault : decimalLimit;

  const isEuro = currencyCode === 'EUR';
  const space = hasSymbol ? '' : ' ';
  const symbol = currenciesSymbol[currencyCode] || '';

  amount = truncateDecimal(amount, precision);

  const newAmount = currency(amount, {
    precision,
    symbol: hasSymbol ? symbol : '',
    decimal: decimalSeparator,
    separator: thousandSeparator,
    pattern: isEuro ? `# ${space}!` : `!${space} #`,
    negativePattern: isEuro ? `- # ${space}!` : `- !${space} #`,
  }).format();

  return newAmount + (hasCode ? ` ${currencyCode}` : '');
}

export default formatCurrency;
